import { NgModule, ApplicationRef } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { HttpModule } from '@angular/http';
import { FormsModule } from '@angular/forms';
import { CurrencyPipe } from '@angular/common';
import { removeNgStyles, createNewHosts } from '@angularclass/hmr';
import { ToasterModule, ToasterService } from 'angular2-toaster';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

import { AppComponent } from './app.component';
import { routing } from './app.routing';
import { TextMaskModule } from 'angular2-text-mask';
import { ClickOutsideModule } from 'ng-click-outside';

import { CustomerInfoComponent } from './customer-info';
import { ProductLinesComponent } from './product-lines';
import { ModelsComponent } from './model-component';
import { SetMarkupComponent } from './set-markup';
import { OptionsAdminComponent } from './options-admin';
import { OptionsConfigurationComponent } from './options-configuration';
import { YetiTitleBarComponent } from './reusable-components/yeti-title-bar';
import { LoadingSpinnerComponent } from './reusable-components/loading-spinner';
import { YetiModalComponent } from './reusable-components/yeti-modal';
import { OrderInformationComponent } from './order-information';
import { ToastrProductLinesComponent } from './reusable-components/toastr-productLines';
import { ToastrModelsComponent } from './reusable-components/toastr-models';
import { SideNavComponent } from './reusable-components/side-nav';
import { OptionsAsideComponent } from './reusable-components/options-aside';
import { QuoteCommentsAsideComponent } from './reusable-components/quote-comments-aside/quote-comments-aside.component';
import { RevertRevisionAsideComponent } from './reusable-components/revert-revision-aside/revert-revision-aside.component';
import { QuoteInfoComponent } from './reusable-components/quote-info';
import { AddCustomOptionComponent } from './reusable-components/add-custom-option';
import { OptionsAdminAsideComponent } from './reusable-components/options-admin-aside';
import { OptionsChangeCategoryAsideComponent } from './reusable-components/options-admin-category-change-aside';
import { ChangeRetailerConfirmationComponent } from './change-retailer-confirmation';
import { OptionComponent } from './option-component';
import { AuditHistoryComponent } from './audit-history';
import { QuoteApprovalsComponent } from './quote-approvals';
import { MchNavigationComponent } from './reusable-components/mch-navigation';
import { DimBackgroundComponent } from './reusable-components/dim-background';
import { ApprovedLockComponent } from './reusable-components/approved-lock/approved-lock.component';
import { EditQuoteButtonComponent } from './reusable-components/edit-quote-button/edit-quote-button.component';
import { EditQuoteWarningComponent } from './reusable-components/edit-quote-warning/edit-quote-warning.component';



import { QuoteApprovalSearchPipe } from './pipes/quote-approval-search.pipe';
import { OrderByPipe } from './pipes/orderby.pipe';
import { WordCasePipe } from './pipes/word-case.pipe';
import { OptionsSearchPipe } from './pipes/options-search.pipe';
import { OptionsAdminSearchPipe } from './pipes/options-admin-search.pipe';
import { SixDigitsPipe } from './pipes/six-digits.pipe';
import { FilterFavoritesPipe } from './pipes/filter-favorites.pipe';
import { PhonePipe } from './pipes/phone-number.pipe';
import { ProductlinesSearchPipe } from './pipes/productlines-search.pipe';
import { SearchModelsPipe } from './pipes/models-search.pipe';
import { FilterModelsPipe } from './pipes/models-filter.pipe';
import { FilterRetailersPipe } from './pipes/filter-retailers.pipe';
import { SortCustomOptionsPipe } from './pipes/sort-custom-options.pipe';
import { ProductLinesUniquePipe } from './pipes/productlines-unique.pipe';

import { AuditHistoryService } from './services/audit-history.service';
import { CustomerService } from './services/customer.service';
import { FacilityService } from './services/facility.service';
import { ModelService } from './services/model.service';
import { OptionsService } from './services/options.service';
import { OptionsAdminService } from './services/options-admin.service';
import { ProductLineService } from './services/product-line.service';
import { ServiceUtilities } from './services/service-utilities';
import { UserFavoritesService } from './services/user-favorites.service';
import { ApplicationStateService } from './services/application-state.service';
import { QuoteService } from './services/quote.service';
import { AdjustmentService } from './services/adjustments.service';
import { LoadingSpinnerService } from './services/loading-spinner.service';
import { QuoteSummaryComponent } from './quote-summary';
import { QuoteCompareComponent } from './quote-compare/quote-compare.component';
import { QuoteCompareColumnComponent } from './quote-compare-column/quote-compare-column.component';
import { DeactivateFormGuard } from './reusable-components/route-control/deactivate-form.guard';
import { OrderInformationService } from './services/order-information.service';
import { PageTitleService } from './services/page-title.service';
import { FilterProductlinesService } from './services/filter-productlines.service';
import { FilterModelsService } from './services/filter-models.service';
import { InformationAsideModelsService } from './services/information-aside-models.service';
import { QuoteSummaryService } from './services/quote-summary.service';
import { OptionsAsideService } from './services/options-aside.service';
import { QuoteInfoService } from './services/quote-info.service';
import { QuoteRevisionInfoService } from './services/quote-revision-info.service';
import { CurrentUserService } from './services/current-user.service';
import { PagerService } from './services/pager.service';
import { CookieService } from 'angular2-cookie/services/cookies.service';
import { AuthDataService } from './services/auth-data.service';
import { AddCustomOptionService } from './services/add-custom-option.service';
import { MinValidator } from './utilities/minNumber-validator';
import { MaxValidator } from './utilities/maxNumber-validator';
import { FocusDirective } from './utilities/focus.directive';
import { FormatUtilities } from './utilities/format-utilities';
import { BrowserWidthService } from './services/browser-width.service';

import { TruExpressService } from './services/truexpress.service';
import { TruExpComponent } from './truexp';
import { SplunkLogService } from './services/splunk-log.service';

import {
  ModalDialogService,
  ModalDialogContainerComponent,
  ConfirmationModalComponent,
  LoadingModalComponent,
  MessageModalComponent,
  ModalDialogComponent
} from './reusable-components/modal-dialog/modal-dialog.service';
import { LoadingIconComponent } from './reusable-components/loading-icon/loading-icon.component';
import { NavigationService } from './services/navigation.service';
import { UserPricingViewService } from './services/user-pricing-view.service';
import { QuoteApprovalService } from './services/quote-approval.service';
import { AuthenticatedHttpService } from './services/authenticatedhttp.service';
import { HttpResponseService } from './services/httpresponse-service';
import { ProspectsService } from './services/prospect.service';
import { DataService } from './services/data.service';
import { ApprovedLockService } from './services/approved-lock.service';
import { MCHAvailableProductLineService } from './services/mchavailableproductline.service';
import { QuoteRevisionService } from './services/quote-revision.service';




import { Angulartics2Module } from 'angulartics2';

import 'hammerjs';
import { TabHostComponent, TabItemComponent } from './reusable-components/tab-menu/tab-host.component';

@NgModule({
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    HttpModule,
    FormsModule,
    routing,
    ToasterModule,
    TextMaskModule,
    ClickOutsideModule,
    NgbModule.forRoot(),
    Angulartics2Module.forRoot({pageTracking: {clearIds: true}}), // Google Analytics
  ],
  declarations: [
    AppComponent,
    CustomerInfoComponent,
    ProductLinesComponent,
    ModelsComponent,
    OptionsConfigurationComponent,
    SetMarkupComponent,
    OrderByPipe,
    WordCasePipe,
    OptionsAdminComponent,
    YetiTitleBarComponent,
    LoadingSpinnerComponent,
    YetiTitleBarComponent,
    YetiModalComponent,
    MinValidator,
    MaxValidator,
    OrderInformationComponent,
    QuoteSummaryComponent,
    QuoteCompareComponent,
    QuoteCompareColumnComponent,
    ToastrProductLinesComponent,
    ToastrModelsComponent,
    FocusDirective,
    OptionsSearchPipe,
    OptionsAdminSearchPipe,
    QuoteApprovalSearchPipe,
    SixDigitsPipe,
    SideNavComponent,
    FilterFavoritesPipe,
    PhonePipe,
    ProductlinesSearchPipe,
    SearchModelsPipe,
    FilterModelsPipe,
    ProductLinesUniquePipe,
    OptionsAsideComponent,
    QuoteCommentsAsideComponent,
    RevertRevisionAsideComponent,
    OptionsAdminAsideComponent,
    OptionsChangeCategoryAsideComponent,
    QuoteInfoComponent,
    PhonePipe,
    AddCustomOptionComponent,
    SortCustomOptionsPipe,
    ModalDialogContainerComponent,
    LoadingModalComponent,
    ConfirmationModalComponent,
    MessageModalComponent,
    ModalDialogComponent,
    LoadingIconComponent,
    FilterRetailersPipe,
    ChangeRetailerConfirmationComponent,
    OptionComponent,
    AuditHistoryComponent,
    QuoteApprovalsComponent,
    MchNavigationComponent,
    DimBackgroundComponent,
    ApprovedLockComponent,
    EditQuoteButtonComponent,
    EditQuoteWarningComponent,
    TabHostComponent,
    TabItemComponent,
    TruExpComponent
  ],
  providers: [
    UserFavoritesService,
    ServiceUtilities,
    OrderByPipe,
    WordCasePipe,
    PhonePipe,
    SixDigitsPipe,
    CustomerService,
    OptionsService,
    FacilityService,
    ProductLineService,
    ModelService,
    ApplicationStateService,
    QuoteService,
    DeactivateFormGuard,
    OrderInformationService,
    AdjustmentService,
    FormatUtilities,
    LoadingSpinnerService,
    PageTitleService,
    FilterProductlinesService,
    ProductlinesSearchPipe,
    FilterModelsService,
    InformationAsideModelsService,
    QuoteSummaryService,
    OptionsAsideService,
    QuoteInfoService,
    QuoteRevisionInfoService,
    CurrentUserService,
    CookieService,
    AuthDataService,
    AddCustomOptionService,
    ModalDialogService,
    FilterRetailersPipe,
    BrowserWidthService,
    OptionsAdminService,
    PagerService,
    OptionsAdminSearchPipe,
    QuoteApprovalSearchPipe,
    OptionsSearchPipe,
    ProductLinesUniquePipe,
    NavigationService,
    UserPricingViewService,
    AuditHistoryService,
    QuoteApprovalService,
    AuthenticatedHttpService,
    HttpResponseService,
    CurrencyPipe,
    ProspectsService,
    DataService,
    ApprovedLockService,
    MCHAvailableProductLineService,
    ToasterService,
      QuoteRevisionService,
      TruExpressService,
    SplunkLogService
  ],
  entryComponents: [
    ToastrProductLinesComponent,
    ToastrModelsComponent,
    MessageModalComponent,
    ConfirmationModalComponent
  ],
  bootstrap: [AppComponent]
})
export class AppModule {
  constructor(public appRef: ApplicationRef) {}
  hmrOnInit(store) {}
  hmrOnDestroy(store) {
    let cmpLocation = this.appRef.components.map(
      cmp => cmp.location.nativeElement
    );
    // recreate elements
    store.disposeOldHosts = createNewHosts(cmpLocation);
    // remove styles
    removeNgStyles();
  }
  hmrAfterDestroy(store) {
    // display new elements
    store.disposeOldHosts();
    delete store.disposeOldHosts;
  }
}



// WEBPACK FOOTER //
// ./~/angular2-template-loader!./~/@angularclass/hmr-loader!./src/app/app.module.ts